import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { ClipboardList, CheckCircle, XCircle, Receipt, Image } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminRequests = () => {
  const [attendanceReqs, setAttendanceReqs] = useState([]);
  const [expenseClaims, setExpenseClaims] = useState([]);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectType, setRejectType] = useState(null);
  const [receiptUrl, setReceiptUrl] = useState(null);
  const [processing, setProcessing] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [attRes, expRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/attendance-requests`, { params: { status: 'pending' }, withCredentials: true }),
        axios.get(`${API_URL}/api/admin/expense-claims`, { params: { status: 'pending' }, withCredentials: true }),
      ]);
      setAttendanceReqs(attRes.data);
      setExpenseClaims(expRes.data);
    } catch (e) { /* silenced */ }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleApproveAttendance = async (requestId) => {
    setProcessing(requestId);
    try {
      await axios.put(`${API_URL}/api/admin/attendance-requests/${requestId}/approve`, {}, { withCredentials: true });
      fetchData();
    } catch (e) { alert(e.response?.data?.detail || 'Errore'); }
    finally { setProcessing(null); }
  };

  const handleRejectAttendance = async (requestId) => {
    setProcessing(requestId);
    try {
      await axios.put(`${API_URL}/api/admin/attendance-requests/${requestId}/reject`,
        { reason: rejectReason }, { withCredentials: true });
      setRejectingId(null);
      setRejectReason('');
      setRejectType(null);
      fetchData();
    } catch (e) { alert(e.response?.data?.detail || 'Errore'); }
    finally { setProcessing(null); }
  };

  const handleApproveExpense = async (claimId) => {
    setProcessing(claimId);
    try {
      await axios.put(`${API_URL}/api/admin/expense-claims/${claimId}/approve`, {}, { withCredentials: true });
      fetchData();
    } catch (e) { alert(e.response?.data?.detail || 'Errore'); }
    finally { setProcessing(null); }
  };

  const handleRejectExpense = async (claimId) => {
    setProcessing(claimId);
    try {
      await axios.put(`${API_URL}/api/admin/expense-claims/${claimId}/reject`,
        { reason: rejectReason }, { withCredentials: true });
      setRejectingId(null);
      setRejectReason('');
      setRejectType(null);
      fetchData();
    } catch (e) { alert(e.response?.data?.detail || 'Errore'); }
    finally { setProcessing(null); }
  };

  const viewReceipt = async (receiptPath) => {
    try {
      const res = await axios.get(`${API_URL}/api/files/${receiptPath}`, {
        withCredentials: true, responseType: 'blob'
      });
      const url = URL.createObjectURL(res.data);
      setReceiptUrl(url);
    } catch (e) { alert('Errore caricamento ricevuta'); }
  };

  const formatDate = (d) => {
    const parts = d.split('-');
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  };

  const totalPending = attendanceReqs.length + expenseClaims.length;

  if (totalPending === 0) return null;

  return (
    <>
      <Card className="border-[#FF9500] border-2 shadow-sm" data-testid="admin-requests-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-bold text-[#09090B] flex items-center justify-between">
            <span className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-[#FF9500]" />
              Richieste Pendenti
            </span>
            <span className="bg-[#FF9500] text-white text-xs font-bold px-2 py-0.5 rounded-full" data-testid="pending-count-badge">
              {totalPending}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Attendance Requests */}
          {attendanceReqs.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-[#0055FF] uppercase tracking-wide">Richieste Presenze</p>
              {attendanceReqs.map(r => (
                <div key={r.request_id} className="p-3 bg-[#0055FF]/5 rounded-lg space-y-2" data-testid={`admin-att-req-${r.request_id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-bold text-[#09090B]">{r.user_name} {r.user_surname}</p>
                      <p className="text-xs text-[#71717A]">{r.dates.length} giorni: {r.dates.map(formatDate).join(', ')}</p>
                      {r.note && <p className="text-xs text-[#71717A] italic mt-0.5">{r.note}</p>}
                    </div>
                  </div>

                  {rejectingId === r.request_id && rejectType === 'attendance' ? (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        placeholder="Motivo rifiuto..."
                        className="flex-1 px-2 py-1 border border-[#E4E4E7] rounded text-xs"
                        data-testid={`reject-reason-${r.request_id}`}
                      />
                      <Button size="sm" variant="destructive" onClick={() => handleRejectAttendance(r.request_id)}
                        disabled={processing === r.request_id} className="text-xs h-7">
                        Conferma
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => { setRejectingId(null); setRejectType(null); }} className="text-xs h-7">
                        Annulla
                      </Button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => handleApproveAttendance(r.request_id)}
                        disabled={processing === r.request_id}
                        data-testid={`approve-att-${r.request_id}`}
                        className="bg-[#34C759] hover:bg-[#2DB84D] text-xs h-7 flex-1">
                        <CheckCircle className="w-3 h-3 mr-1" />Approva
                      </Button>
                      <Button size="sm" variant="outline"
                        onClick={() => { setRejectingId(r.request_id); setRejectType('attendance'); setRejectReason(''); }}
                        disabled={processing === r.request_id}
                        data-testid={`reject-att-${r.request_id}`}
                        className="text-[#FF3B30] border-[#FF3B30] hover:bg-red-50 text-xs h-7 flex-1">
                        <XCircle className="w-3 h-3 mr-1" />Rifiuta
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Expense Claims */}
          {expenseClaims.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-[#FF9500] uppercase tracking-wide">Spese Extra</p>
              {expenseClaims.map(c => (
                <div key={c.claim_id} className="p-3 bg-[#FF9500]/5 rounded-lg space-y-2" data-testid={`admin-exp-claim-${c.claim_id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-bold text-[#09090B]">{c.user_name} {c.user_surname}</p>
                      <p className="text-xs text-[#71717A]">€{c.amount.toFixed(2)} — {c.description}</p>
                    </div>
                    {c.receipt_path && (
                      <Button size="sm" variant="ghost" onClick={() => viewReceipt(c.receipt_path)}
                        data-testid={`view-receipt-${c.claim_id}`}
                        className="text-[#0055FF] h-7 text-xs">
                        <Image className="w-3 h-3 mr-1" />Ricevuta
                      </Button>
                    )}
                  </div>

                  {rejectingId === c.claim_id && rejectType === 'expense' ? (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        placeholder="Motivo rifiuto..."
                        className="flex-1 px-2 py-1 border border-[#E4E4E7] rounded text-xs"
                        data-testid={`reject-reason-${c.claim_id}`}
                      />
                      <Button size="sm" variant="destructive" onClick={() => handleRejectExpense(c.claim_id)}
                        disabled={processing === c.claim_id} className="text-xs h-7">
                        Conferma
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => { setRejectingId(null); setRejectType(null); }} className="text-xs h-7">
                        Annulla
                      </Button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => handleApproveExpense(c.claim_id)}
                        disabled={processing === c.claim_id}
                        data-testid={`approve-exp-${c.claim_id}`}
                        className="bg-[#34C759] hover:bg-[#2DB84D] text-xs h-7 flex-1">
                        <CheckCircle className="w-3 h-3 mr-1" />Approva
                      </Button>
                      <Button size="sm" variant="outline"
                        onClick={() => { setRejectingId(c.claim_id); setRejectType('expense'); setRejectReason(''); }}
                        disabled={processing === c.claim_id}
                        data-testid={`reject-exp-${c.claim_id}`}
                        className="text-[#FF3B30] border-[#FF3B30] hover:bg-red-50 text-xs h-7 flex-1">
                        <XCircle className="w-3 h-3 mr-1" />Rifiuta
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Receipt modal */}
      {receiptUrl && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => { URL.revokeObjectURL(receiptUrl); setReceiptUrl(null); }}>
          <div className="bg-white rounded-lg p-2 max-w-lg max-h-[80vh] overflow-auto" onClick={e => e.stopPropagation()}>
            <img src={receiptUrl} alt="Ricevuta" className="w-full rounded" data-testid="receipt-modal-image" />
            <Button variant="outline" className="w-full mt-2" onClick={() => { URL.revokeObjectURL(receiptUrl); setReceiptUrl(null); }}>
              Chiudi
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminRequests;
